import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { VideoUpload, VideoPlayer, TranscriptTimeline, SessionControls } from '../capture';
import { useSessionStore } from '../session/store';
import { useCaptureSession } from '../session/useCaptureSession';
import { probeCapabilities } from '../render/probe';
import { useTokenSource } from '../api/token';
import type { FootageAsset, SignedMedia } from '../api/client';

/**
 * Capture route — the input half of Callout:
 *   footage upload (+ consent gate) → playback → mic capture → live transcript → session states
 *
 * The wall-clock sync engine (src/sync) anchors every audio segment to `video.currentTime`, so
 * transcript timing never depends on the transcription API.
 */
export function CaptureRoute() {
  const capture = useCaptureSession();
  const tokens = useTokenSource();

  const footage = useSessionStore((s) => s.footage);
  const media = useSessionStore((s) => s.media);
  const transcript = useSessionStore((s) => s.transcriptSegments);
  const setFootage = useSessionStore((s) => s.setFootage);
  const setCapabilityReport = useSessionStore((s) => s.setCapabilityReport);
  const capabilityReport = useSessionStore((s) => s.capabilityReport);

  const [currentTime, setCurrentTime] = useState(0);
  const [tokenPresent, setTokenPresent] = useState<boolean>(false);

  // Capability probe at session start (plan §2.5) — stub here, full probe in the RENDER card.
  useEffect(() => {
    let cancelled = false;
    void probeCapabilities().then((report) => {
      if (!cancelled) setCapabilityReport(report);
    });
    return () => {
      cancelled = true;
    };
  }, [setCapabilityReport]);

  useEffect(() => {
    void tokens.getToken().then((token) => setTokenPresent(Boolean(token)));
  }, [tokens, transcript.length]);

  // Feed the transcript timeline's playhead highlight.
  useEffect(() => {
    const video = capture.videoRef.current;
    if (!video) return;
    const onTime = () => setCurrentTime(video.currentTime);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('seeked', onTime);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('seeked', onTime);
    };
  }, [capture.videoRef, footage]);

  const onUploaded = useCallback(
    (uploaded: FootageAsset, signed: SignedMedia) => setFootage(uploaded, signed),
    [setFootage],
  );

  const canStart = Boolean(footage) && capture.status === 'idle';
  const canEnd = capture.status === 'recording';

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-5">
        <VideoUpload onUploaded={onUploaded} getToken={tokens.getToken} />
        <VideoPlayer footage={footage} media={media} videoRef={capture.videoRef} />
        <SessionControls
          status={capture.status}
          micMode={capture.micMode}
          micState={capture.micState}
          segments={capture.segments}
          chunks={capture.chunks.length}
          busy={capture.busy}
          error={capture.error}
          canStart={canStart}
          canEnd={canEnd}
          onStart={() => void capture.startSession()}
          onEnd={() => void capture.endSession()}
          onNewSession={capture.newSession}
          onToggleMic={() => void capture.toggleMic()}
          onModeChange={capture.setMicMode}
          onAdvance={() => void capture.advancePipeline()}
        />
      </div>

      <div className="space-y-5">
        <TranscriptTimeline
          segments={transcript}
          currentTimeS={currentTime}
          onSeek={capture.seekTo}
        />
        <section className="rounded-lg border border-chalk-200 bg-white/70 p-5 text-sm shadow-sm">
          <h2 className="mb-3 text-lg font-semibold tracking-tight">Clock sync</h2>
          <p data-testid="sync-status" className="font-mono text-xs">
            anchors {capture.syncEngine.anchorPairs.length} · pause{' '}
            {capture.syncEngine.totalPauseDurationMs.toFixed(0)} ms
          </p>
          <p className="mt-2 text-xs text-charcoal-500">
            Wall-clock anchoring (<code className="font-mono">performance.now()</code>) — the
            transcription API is never asked for word timestamps.
          </p>
          {capabilityReport ? (
            <p data-testid="capability-report" className="mt-2 text-xs text-charcoal-500">
              WebCodecs probe:{' '}
              {String((capabilityReport as { renderer?: string }).renderer ?? 'unknown')}
            </p>
          ) : null}
          {!tokenPresent && (
            <p data-testid="token-hint" className="mt-3 text-xs text-copper-600">
              <NavLink to="/sign-in" className="underline">
                Sign in
              </NavLink>{' '}
              to upload footage and record sessions.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
